import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import {
  BillingStatusResponse,
  CheckoutSessionRequest,
  CheckoutSessionResponse,
  CheckoutSessionVerifyRequest,
  CheckoutSessionVerifyResponse,
  CustomerPortalResponse,
} from '@/types/billing';

export function useBillingStatus(
  workspaceId?: string,
  options?: { refetchInterval?: number | false; enabled?: boolean },
) {
  return useQuery<BillingStatusResponse>({
    queryKey: ['billing', 'status', workspaceId],
    queryFn: async () => {
      const params = workspaceId ? { workspace_id: workspaceId } : {};
      const { data } = await api.get('/billing/status', { params });
      return data;
    },
    staleTime: 1000 * 60 * 2,
    refetchOnWindowFocus: true,
    refetchInterval: options?.refetchInterval,
    enabled: options?.enabled,
  });
}

export function useCreateCheckoutSession() {
  const queryClient = useQueryClient();
  return useMutation<CheckoutSessionResponse, Error, CheckoutSessionRequest & { workspaceId?: string }>({
    mutationFn: async ({ workspaceId, ...payload }) => {
      const params = workspaceId ? { workspace_id: workspaceId } : {};
      const { data } = await api.post('/billing/create-checkout-session', payload, { params });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing'] });
    },
  });
}

export function useVerifyCheckoutSession() {
  const queryClient = useQueryClient();
  return useMutation<
    CheckoutSessionVerifyResponse,
    Error,
    CheckoutSessionVerifyRequest & { workspaceId?: string }
  >({
    mutationFn: async ({ workspaceId, session_id }) => {
      const params = workspaceId ? { workspace_id: workspaceId } : {};
      const { data } = await api.post(
        '/billing/verify-checkout-session',
        { session_id },
        { params },
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing'] });
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      queryClient.invalidateQueries({ queryKey: ['current-workspace'] });
    },
  });
}

export function useCustomerPortal() {
  return useMutation<CustomerPortalResponse, Error, { workspaceId?: string; returnUrl?: string }>({
    mutationFn: async ({ workspaceId, returnUrl }) => {
      const params = workspaceId ? { workspace_id: workspaceId } : {};
      const { data } = await api.post('/billing/create-portal-session', { return_url: returnUrl }, { params });
      return data;
    },
  });
}

